import React, { useCallback, useMemo, useState } from "react";
import { useNavigate, useOutletContext, useSearchParams } from "react-router-dom";
import { FiCalendar, FiRefreshCw, FiAlertCircle } from "react-icons/fi";
import DataTable from "../common/DataTable";
import TablePagination from "../common/TablePagination";
import StatusBadge from "../common/StatusBadge";
import useAdminResource from "../../hooks/useAdminResource";
import useDebouncedValue from "../../hooks/useDebouncedValue";
import adminUserService from "../../services/adminUserService";
import { formatWhen, formatDateTime, formatDate, formatPause } from "./scheduleFormat";
import { ReadOnlyToggle, ScheduleDevices } from "./ScheduleBits";
import { ScheduleDetailModal } from "./DetailModals";
import { deviceDisplayName } from "../../utils/deviceName";

const STATUSES = [
  ["all", "All statuses"],
  ["active", "Active"],
  ["pause", "Paused"],
  ["draft", "Draft"],
  ["archived", "Archived"],
];
const CRON_STATUSES = [
  ["all", "All run states"],
  ["active", "Active"],
  ["processing", "Processing"],
  ["completed", "Completed"],
  ["failed", "Failed"],
  ["disabled", "Disabled"],
];
const SORTS = [
  ["createdAt", "Newest"],
  ["name", "Name"],
  ["when.nextRunAt", "Next run"],
  ["updatedAt", "Recently updated"],
];

export default function UserSchedulesTab() {
  const { userId, deviceIndex } = useOutletContext();
  const navigate = useNavigate();
  const basePath = `/admin/dash/users/${userId}`;

  const [searchParams, setSearchParams] = useSearchParams();
  const search = searchParams.get("search") || "";
  const status = searchParams.get("status") || "all";
  const cronstatus = searchParams.get("cronstatus") || "all";
  const sortBy = searchParams.get("sortBy") || "createdAt";
  // Set when arriving from a device row — scopes the list to one device.
  const deviceId = searchParams.get("deviceId") || "";
  const page = Number(searchParams.get("page") || 1);
  const limit = Number(searchParams.get("limit") || 20);

  const debouncedSearch = useDebouncedValue(search, 300);
  // The detail was an inline row expansion, which pushed the rest of the table
  // down and squeezed the wide action grid into a single table cell. Modal now.
  const [detailSchedule, setDetailSchedule] = useState(null);

  const patchParams = useCallback(
    (patch) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        for (const [k, v] of Object.entries(patch)) {
          if (v === undefined || v === null || v === "" || v === "all") next.delete(k);
          else next.set(k, String(v));
        }
        if (!("page" in patch)) next.delete("page");
        return next;
      });
    },
    [setSearchParams]
  );

  const fetcher = useCallback(
    () =>
      adminUserService.fetchUserSchedules(userId, {
        page,
        limit,
        search: debouncedSearch,
        status,
        cronstatus,
        deviceId,
        sortBy,
        sortOrder: sortBy === "name" ? "asc" : "desc",
      }),
    [userId, page, limit, debouncedSearch, status, cronstatus, deviceId, sortBy]
  );

  const { data, meta, state, error, refetch, dismissError } = useAdminResource(
    fetcher,
    [userId, page, limit, debouncedSearch, status, cronstatus, deviceId, sortBy]
  );

  const schedules = data || [];

  const columns = useMemo(
    () => [
      {
        id: "name",
        header: "Schedule",
        cell: ({ row }) => {
          const s = row.original;
          return (
            <div className="min-w-[180px]">
              <div className="flex items-center gap-1.5">
                <span className="font-semibold text-slate-800">{s.name}</span>
                {s.smartRestore && (
                  <span title="Smart Restore is on" className="text-indigo-500">✨</span>
                )}
                {/* There is no `hasError` field — the error signal is the
                    subscription `signal` plus per-action errorCodes. */}
                {s.signal === "red" && (
                  <FiAlertCircle
                    size={14}
                    className="text-red-500"
                    title="Subscription expired — schedule mutation is blocked"
                  />
                )}
              </div>
              <p className="mt-0.5 text-[11px] text-slate-400">{s.roomSummary}</p>
            </div>
          );
        },
      },
      {
        id: "status",
        header: "Status",
        cell: ({ row }) => {
          const s = row.original;
          const pauseText = s.status === "pause" ? formatPause(s.pauseSummary) : null;
          return (
            <div>
              <StatusBadge kind="scheduleStatus" value={s.status} />
              {pauseText && (
                <p className="mt-1 text-[11px] text-amber-600">{pauseText}</p>
              )}
              {s.status === "archived" && s.archivedAt && (
                <p className="mt-1 text-[11px] text-slate-400">
                  {formatDate(s.archivedAt)}
                  {s.statusBeforeArchive && ` · was ${s.statusBeforeArchive}`}
                </p>
              )}
            </div>
          );
        },
      },
      {
        id: "cronstatus",
        header: "Run state",
        cell: ({ row }) => <StatusBadge kind="cronStatus" value={row.original.cronstatus} />,
      },
      {
        id: "when",
        header: "When",
        cell: ({ row }) => (
          <div className="min-w-[160px] text-sm text-slate-600">
            {formatWhen(row.original)}
            {row.original.when?.timezone && (
              <p className="text-[11px] text-slate-400">{row.original.when.timezone}</p>
            )}
          </div>
        ),
      },
      {
        id: "nextRun",
        header: "Next run",
        cell: ({ row }) => {
          const s = row.original;
          // A draft has no computed next run — say so rather than showing "—",
          // which reads as "we don't know".
          if (s.status === "draft") {
            return <span className="text-sm text-slate-400">Not scheduled</span>;
          }
          return (
            <span className="text-sm text-slate-600">{formatDateTime(s.nextRunAt)}</span>
          );
        },
      },
      {
        id: "devices",
        header: "Devices",
        cell: ({ row }) => (
          <ScheduleDevices
            schedule={row.original}
            deviceIndex={deviceIndex}
            basePath={basePath}
            navigate={navigate}
          />
        ),
      },
      {
        id: "toggle",
        header: "",
        cell: ({ row }) => <ReadOnlyToggle status={row.original.status} />,
      },
      {
        id: "expand",
        header: "",
        cell: ({ row }) => (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setDetailSchedule(row.original);
            }}
            className="rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-700 transition-colors hover:bg-slate-50"
          >
            Details
          </button>
        ),
      },
    ],
    [deviceIndex, basePath, navigate]
  );

  const isFiltered =
    Boolean(search) || status !== "all" || cronstatus !== "all" || Boolean(deviceId);

  const clearFilters = () => setSearchParams(new URLSearchParams());

  const select =
    "rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none";

  const filterDevice = deviceId ? deviceIndex?.byVendorId?.[deviceId] : null;

  return (
    <div>
      {deviceId && (
        <div className="mb-3 flex items-center gap-2 rounded-lg bg-indigo-50 px-3 py-2 text-sm text-indigo-800">
          Showing only schedules that drive{" "}
          <span className="font-semibold">
            {deviceDisplayName(filterDevice, deviceId)}
          </span>
          <button
            type="button"
            onClick={() => patchParams({ deviceId: "" })}
            className="ml-auto text-xs font-medium underline"
          >
            Clear
          </button>
        </div>
      )}

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <input
          type="search"
          value={search}
          onChange={(e) => patchParams({ search: e.target.value })}
          placeholder="Search schedule name…"
          className={`${select} min-w-[200px] flex-1`}
        />
        <select value={status} onChange={(e) => patchParams({ status: e.target.value })} className={select}>
          {STATUSES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <select value={cronstatus} onChange={(e) => patchParams({ cronstatus: e.target.value })} className={select}>
          {CRON_STATUSES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <select value={sortBy} onChange={(e) => patchParams({ sortBy: e.target.value })} className={select}>
          {SORTS.map(([v, l]) => <option key={v} value={v}>Sort: {l}</option>)}
        </select>
        <button
          type="button"
          onClick={() => refetch()}
          className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
        >
          <FiRefreshCw size={14} className={state === "loading" ? "animate-spin" : ""} />
          Refresh
        </button>
      </div>

      <DataTable
        columns={columns}
        data={schedules}
        state={state}
        error={error}
        onRetry={() => refetch()}
        onDismissError={dismissError}
        getRowId={(row) => String(row.scheduleId)}
        onRowClick={(row) => setDetailSchedule(row)}
        emptyIcon={FiCalendar}
        emptyTitle={isFiltered ? "No schedules match these filters" : "No schedules yet"}
        emptyMessage={
          isFiltered
            ? "Try widening the filters above."
            : "This user has not created any schedules."
        }
        isFiltered={isFiltered}
        onClearFilters={clearFilters}
      />

      <TablePagination
        page={meta.page}
        totalPages={meta.totalPages}
        total={meta.total}
        limit={meta.limit}
        onPageChange={(p) => patchParams({ page: p })}
        onLimitChange={(l) => patchParams({ limit: l, page: 1 })}
      />

      <ScheduleDetailModal
        open={Boolean(detailSchedule)}
        onClose={() => setDetailSchedule(null)}
        userId={userId}
        schedule={detailSchedule}
        deviceIndex={deviceIndex}
      />
    </div>
  );
}
